import { onBeforeUnmount, onMounted } from 'vue'
import { usePipe, PipeDisplayMode } from '@render/composables/usePipe'

const MAX_ATTEMPTS = 120

// Android in-app browsers (Instagram/Facebook WebView) auto-zoom an input up to
// the viewport's `maximum-scale` when it is focused programmatically during a
// page transition — with our `maximum-scale=5.0` viewport that shows up as a
// jarring x5 zoom. Locking `maximum-scale=1.0` across the focus call suppresses
// the auto-zoom; we restore the original viewport on blur so users keep their
// ability to pinch-zoom afterwards.
function focusWithoutZoom(el: HTMLElement) {
  const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
  if (!viewport) {
    el.focus()
    return
  }

  const original = viewport.getAttribute('content') ?? ''
  const locked = /maximum-scale\s*=/.test(original)
    ? original.replace(/maximum-scale\s*=\s*[^,]+/, 'maximum-scale=1.0')
    : `${original}, maximum-scale=1.0`

  viewport.setAttribute('content', locked)
  el.addEventListener('blur', () => viewport.setAttribute('content', original), { once: true })

  el.focus()
}

export function useAutoFocus(): void {
  const pipe = usePipe()
  let unsub: (() => void) | undefined
  let runId = 0

  function focusFirstEligible() {
    if (typeof window === 'undefined') return
    if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return

    const currentRun = ++runId

    // Get first input on the page
    const el = document.querySelector<HTMLElement>('input[data-autofocus="true"]')
    if (!el) return

    // Poll instead of listening for events: as some elements may
    // be not visible at the beginning.
    let attempts = 0
    const poll = () => {
      if (currentRun !== runId) return

      attempts++

      if (attempts > MAX_ATTEMPTS) return

      const offset = el.offsetParent as HTMLElement | null
      if (!offset) {
        requestAnimationFrame(poll)
        return
      }

      const cs = getComputedStyle(offset)
      if (cs.opacity === '1' && cs.visibility === 'visible') {
        focusWithoutZoom(el)
        return
      }

      requestAnimationFrame(poll)
    }
    requestAnimationFrame(poll)
  }

  onMounted(() => {
    unsub = pipe.subscribe('navigation:show', focusFirstEligible)
  })

  onBeforeUnmount(() => {
    runId++
    unsub?.()
  })
}
